/**
 * The one toast channel. Mounted once in main.tsx, above the router.
 *
 * Anything that wants to say something calls `useToast()` — there is no
 * `onToast` prop to thread and no per-page `<Toast>` to render.
 *
 * Placement: bottom-LEFT of the dashboard content column, not bottom-centre of
 * the viewport. Bottom-centre put the stack under the bottom-left corner of any
 * right-side drawer at 1440px (the Agent settings drawer is the one the owner
 * hit on 2026-09-16), and "centre" is a lie on this layout anyway — the sidebar
 * owns 60–260px of the left edge, so a viewport-centred toast is never centred
 * on the content.
 *
 * The column is found, not computed. `<ToastAnchor />` in DashboardLayout marks
 * the content column and the stack portals into it, so the sidebar's three
 * widths (60 collapsed / 220 / 260 on a product route) and its collapse state —
 * which lives in DarkSidebar's own localStorage hook, not in the layout — stay
 * in exactly one place. Surfaces with no sidebar (walkthrough player, auth)
 * register no anchor and fall back to bottom-left of the viewport at the same
 * inset.
 */
import { createContext, useContext, useMemo, useRef, useState, useSyncExternalStore, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { ToastItem } from './ToastItem';
import { createToastStore, type ToastOptions, type ToastVariant } from './toastStore';

export interface ToastApi {
  success: (message: string, options?: ToastOptions) => string;
  info: (message: string, options?: ToastOptions) => string;
  warning: (message: string, options?: ToastOptions) => string;
  error: (message: string, options?: ToastOptions) => string;
  show: (variant: ToastVariant, message: string, options?: ToastOptions) => string;
  dismiss: (id: string) => void;
}

const ToastContext = createContext<ToastApi | null>(null);
const ToastAnchorContext = createContext<((el: HTMLElement | null) => void) | null>(null);

/**
 * Marks the box the toast stack should sit in the bottom-left of. Render it
 * inside a `relative` container; it covers that container but never takes a
 * click. Outside a ToastProvider it is inert, so a layout can mount it
 * unconditionally.
 */
export function ToastAnchor() {
  const setAnchor = useContext(ToastAnchorContext);
  return <div ref={setAnchor ?? undefined} className="pointer-events-none absolute inset-0 z-[120]" />;
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const storeRef = useRef<ReturnType<typeof createToastStore> | null>(null);
  if (!storeRef.current) storeRef.current = createToastStore();
  const store = storeRef.current;

  // A ref callback (not useRef) so mounting/unmounting the anchor re-renders the
  // stack into or out of it — the dashboard layout mounts and unmounts on every
  // login and logout.
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  const visible = useSyncExternalStore(store.subscribe, store.getVisible, store.getVisible);

  // Stable for the life of the provider, so a consumer can put `toast` in a
  // dependency array without re-running the effect on every render.
  const api = useMemo<ToastApi>(() => ({
    success: (message, options) => store.push(message, 'success', options),
    info: (message, options) => store.push(message, 'info', options),
    warning: (message, options) => store.push(message, 'warning', options),
    error: (message, options) => store.push(message, 'error', options),
    show: (variant, message, options) => store.push(message, variant, options),
    dismiss: store.dismiss,
  }), [store]);

  // Always mounted, even empty: a live region inserted at the same moment as its
  // content is not reliably announced. `flex-col-reverse` with the oldest first
  // in the array puts the newest on top.
  const stack = (
    <ol
      aria-label="Notifications"
      className={
        'pointer-events-none z-[120] flex flex-col-reverse gap-2 ' +
        (anchor
          ? 'absolute bottom-6 left-6 w-[min(26rem,calc(100%-3rem))]'
          : 'fixed bottom-6 left-6 w-[min(26rem,calc(100vw-3rem))]')
      }
    >
      {visible.map((t) => (
        <ToastItem key={t.id} toast={t} onDismiss={store.dismiss} />
      ))}
    </ol>
  );

  return (
    <ToastContext.Provider value={api}>
      <ToastAnchorContext.Provider value={setAnchor}>
        {children}
        {anchor ? createPortal(stack, anchor) : stack}
      </ToastAnchorContext.Provider>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const api = useContext(ToastContext);
  if (!api) throw new Error('useToast() requires <ToastProvider> — it is mounted in main.tsx');
  return api;
}
